import { createSlice } from '@reduxjs/toolkit';
import { omit } from 'ramda';

import { FetchStatus } from 'src/enums/FetchStatus';
import TimeSheetsRepository, { IndexParams } from 'src/repositories/talents/TimeSheetsAdjustmentsRepository';
import { Meta } from 'src/types/meta';
import { TalentAdjustedTimeSheet } from 'src/types/resources/TimeSheet';
import { ListResponse } from 'src/types/utils';
import { createAsyncAction } from 'src/utils/createAsyncAction';

export type TimeSheetsAdjustmentsSliceStateType = {
  meta: Meta;
  timeSheetsAdjustments: TalentAdjustedTimeSheet[];
  index: {
    fetchStatus: FetchStatus;
    error: unknown;
  };
};

export type TimeSheetsAdjustmentsSliceActionsType = {
  loadTimeSheetsAdjustments: (params?: IndexParams) => {
    unwrap: () => Promise<ListResponse<TalentAdjustedTimeSheet>>;
  };
  resetTimeSheetsAdjustments: () => void;
};

const initialState: TimeSheetsAdjustmentsSliceStateType = {
  meta: {} as Meta,
  timeSheetsAdjustments: [],
  index: {
    fetchStatus: FetchStatus.idle,
    error: null,
  },
};

export const loadTimeSheetsAdjustments = createAsyncAction(
  'talents/timeSheetsAdjustments/index',
  TimeSheetsRepository.index,
);

const TimeSheetsAdjustmentsSlice = createSlice({
  name: 'talents/timeSheetsAdjustments',
  initialState,
  reducers: {
    resetTimeSheetsAdjustments(state) {
      state.timeSheetsAdjustments = [];
    },
  },
  extraReducers: builder => {
    builder.addCase(loadTimeSheetsAdjustments.pending, state => {
      state.index.fetchStatus = FetchStatus.pending;
    });
    builder.addCase(loadTimeSheetsAdjustments.fulfilled, (state, { payload }) => {
      state.index.fetchStatus = FetchStatus.fulfilled;
      state.meta = omit(['results'], payload);
      state.timeSheetsAdjustments = payload.results;
    });
    builder.addCase(loadTimeSheetsAdjustments.rejected, state => {
      state.index.fetchStatus = FetchStatus.failed;
    });
  },
});

const {
  actions: { resetTimeSheetsAdjustments },
} = TimeSheetsAdjustmentsSlice;

export { resetTimeSheetsAdjustments };

export default TimeSheetsAdjustmentsSlice.reducer;
